import {
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useRegisterAPIService } from '@elfsight/widget-js-api';
import { useStorage } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import {
  API_BAR_CLOSE_METHOD_NAME,
  API_BAR_OPEN_METHOD_NAME,
  API_BAR_SERVICE_NAME
} from '../constants';
import { isInlineLayout } from '../utils';
import {
  type CloseOptions,
  type CountdownTimerState,
  CountdownTimerStateContext
} from './countdown-timer-state-context';

const EDIT_MODE_RESET_DELAY = 1000;

export function CountdownTimerStateProvider({ children }: PropsWithChildren) {
  const { layout, showCloseButton, editMode } = useSettings();
  const [
    isClosedStorage,
    { set: setIsClosedStorage, remove: removeIsClosedStorage }
  ] = useStorage<boolean>('closed', false);
  const [isClosedSession, setIsClosedSession] = useState(false);
  const [isHidden, setIsHidden] = useState(false);

  const isBar = !isInlineLayout(layout);

  useEffect(() => {
    if (!isClosedStorage) {
      setIsClosedSession(false);
    }
  }, [isClosedStorage]);

  const openWidget = useCallback(() => {
    setIsHidden(false);
    setIsClosedSession(false);
    removeIsClosedStorage();
  }, [removeIsClosedStorage]);

  const hideWidget = useCallback(() => {
    setIsHidden(true);

    if (editMode) {
      setTimeout(openWidget, EDIT_MODE_RESET_DELAY);
    }
  }, [editMode, openWidget]);

  const closeWidget = useCallback(
    ({ memorize = false }: CloseOptions = {}) => {
      if (memorize && !editMode) {
        setIsClosedStorage(true);
      }

      setIsClosedSession(true);

      if (editMode) {
        setTimeout(openWidget, EDIT_MODE_RESET_DELAY);
      }
    },
    [setIsClosedStorage, editMode, openWidget]
  );

  const state: CountdownTimerState = useMemo(() => {
    if (isHidden) {
      return 'hidden';
    }

    const hasClosedState = isBar && showCloseButton;
    if (hasClosedState && (isClosedStorage || isClosedSession)) {
      return 'closed';
    }

    return 'open';
  }, [isHidden, isBar, showCloseButton, isClosedStorage, isClosedSession]);

  useRegisterAPIService(
    API_BAR_SERVICE_NAME,
    () => ({
      [API_BAR_CLOSE_METHOD_NAME]: closeWidget,
      [API_BAR_OPEN_METHOD_NAME]: openWidget
    }),
    [closeWidget, openWidget]
  );

  const context = useMemo(
    () => ({
      state,
      closeWidget,
      hideWidget,
      openWidget
    }),
    [state, closeWidget, hideWidget, openWidget]
  );

  return (
    <CountdownTimerStateContext.Provider value={context}>
      {children}
    </CountdownTimerStateContext.Provider>
  );
}
